import Button from 'devextreme-react/button';
import DataGrid, { Column, Editing, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { OPS_GRID_TOOLBAR_DESKTOP_ONLY, OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome, showErrorToast, showSuccessToast, useResponsivePopupOptions } from '@jarvis/platform-ui';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { CompanyHoliday } from '../../../api/companyHolidays';
import {
  createAdminCompanyHoliday,
  deleteAdminCompanyHoliday,
  listAdminCompanyHolidays,
  updateAdminCompanyHoliday
} from '../../../api/companyHolidays';

const GRID_STATE_KEY = 'webopsui:company-holidays-grid:v3';
const GRID_COLUMNS = ['holidayDate', 'name'] as const;

const fieldLabel: CSSProperties = {
  fontWeight: 600,
  marginBottom: 6,
  fontSize: 13,
  display: 'block'
};

function formatDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseHolidayRowDate(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

type HolidayRow = CompanyHoliday & { holidayDateLocal: Date };

export function CompanyHolidaysAdminPage() {
  const gridRef = useRef<DataGridRef>(null);
  const currentYear = new Date().getFullYear();
  const yearItems = useMemo(
    () => Array.from({ length: 7 }, (_, i) => ({ value: currentYear - 3 + i, text: String(currentYear - 3 + i) })),
    [currentYear]
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState<Date>(new Date(currentYear, 0, 1));
  const [addName, setAddName] = useState('');
  const [saving, setSaving] = useState(false);
  const addHolidayPopupOptions = useResponsivePopupOptions({ width: 420, height: 'auto' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAdminCompanyHolidays(selectedYear);
      setRows(
        data.map((row) => ({
          ...row,
          holidayDateLocal: parseHolidayRowDate(row.holidayDate)
        }))
      );
    } catch (e) {
      setRows([]);
      showErrorToast(e instanceof Error ? e.message : 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setAddDate(new Date(selectedYear, 0, 1));
  }, [selectedYear]);

  async function submitAdd() {
    if (!addName.trim()) {
      showErrorToast('Holiday name is required.');
      return;
    }
    setSaving(true);
    try {
      await createAdminCompanyHoliday({
        holidayDate: formatDateOnly(addDate),
        name: addName.trim()
      });
      showSuccessToast('Holiday added.');
      setAddOpen(false);
      setAddName('');
      await load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to add holiday');
    } finally {
      setSaving(false);
    }
  }

  const holidaysToolbarMenuItems = useMemo(
    () => [{ id: 'add-holiday', text: 'Add holiday', icon: 'add', onClick: () => setAddOpen(true) }],
    []
  );

  return (
    <div className="ops-fill-page">
      <header className="ops-fill-page__header" style={{ padding: '0.75rem 1rem 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Company Holidays</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: 14, color: 'var(--text-muted, #6b7280)' }}>
              Official company holidays excluded from employee PTO requests.
            </p>
          </div>
          <div style={{ minWidth: 120 }}>
            <span style={fieldLabel}>Year</span>
            <SelectBox
              items={yearItems}
              displayExpr="text"
              valueExpr="value"
              value={selectedYear}
              onValueChanged={(e) => {
                if (typeof e.value === 'number') setSelectedYear(e.value);
              }}
            />
          </div>
        </div>
      </header>

      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnReordering={false}
          allowColumnResizing
          {...OPS_SITE_GRID_PROPS}
          noDataText={`No holidays configured for ${selectedYear}.`}
          onRowUpdating={async (e) => {
            const data = e.oldData as HolidayRow;
            const next = e.newData as HolidayRow;
            try {
              await updateAdminCompanyHoliday(data.id, {
                holidayDate: next.holidayDate ?? formatDateOnly(next.holidayDateLocal),
                name: next.name
              });
              showSuccessToast('Holiday updated.');
              await load();
            } catch (err) {
              e.cancel = true;
              showErrorToast(err instanceof Error ? err.message : 'Failed to update holiday');
            }
          }}
          onRowRemoving={async (e) => {
            const data = e.data as HolidayRow;
            try {
              await deleteAdminCompanyHoliday(data.id);
              showSuccessToast('Holiday removed.');
            } catch (err) {
              e.cancel = true;
              showErrorToast(err instanceof Error ? err.message : 'Failed to delete holiday');
            }
          }}
        >
          <OpsSiteGridChrome
            stateStorageKey={GRID_STATE_KEY}
            stateColumns={GRID_COLUMNS}
            searchPlaceholder="Search holidays…"
            pageSize={25}
          />
          <Editing mode="row" allowUpdating allowDeleting useIcons />
          <Toolbar>
            <Item location="before" cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}>
              <Button text="Add holiday" icon="add" type="default" onClick={() => setAddOpen(true)} />
            </Item>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh holidays"
              extraMenuItems={holidaysToolbarMenuItems}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <Column
            dataField="holidayDateLocal"
            caption="Date"
            dataType="date"
            sortOrder="asc"
            width={160}
            setCellValue={(rowData, value) => {
              if (value instanceof Date) {
                rowData.holidayDateLocal = value;
                rowData.holidayDate = formatDateOnly(value);
              }
            }}
          />
          <Column dataField="name" caption="Name" minWidth={180} />
        </DataGrid>
      </div>

      <Popup
        visible={addOpen}
        onHiding={() => setAddOpen(false)}
        showTitle
        title="Add company holiday"
        {...addHolidayPopupOptions}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
          <div>
            <span style={fieldLabel}>Date</span>
            <DateBox
              type="date"
              value={addDate}
              onValueChanged={(e) => {
                if (e.value instanceof Date) setAddDate(e.value);
              }}
            />
          </div>
          <div>
            <span style={fieldLabel}>Name</span>
            <TextBox value={addName} onValueChanged={(e) => setAddName(String(e.value ?? ''))} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button text="Cancel" stylingMode="outlined" onClick={() => setAddOpen(false)} disabled={saving} />
            <Button text="Add" type="default" onClick={() => void submitAdd()} disabled={saving} />
          </div>
        </div>
      </Popup>
    </div>
  );
}
